import { AuraBaseDesignTokens, AuraBaseTokenSections } from "@primeuix/themes/aura/base";
import type { ColorScale } from "@primeuix/styled";

// Rebrand 2026: the Cleverly DS adds wine/sand/grey color scales that Aura's
// closed Primitive type doesn't know — widen it (runtime is key-driven, this
// is purely a typing concern).
type CleverlyPrimitive = AuraBaseTokenSections.Primitive & {
    wine?: ColorScale;
    sand?: ColorScale;
    grey?: ColorScale;
};

export const primitive: CleverlyPrimitive = {
    // Brand guide radius scale: small 4 · medium 10 · large 16 · xlarge 20 · full 100
    // (full = extend.borderRadius.rounded). No other steps (O9): sm had 8px with
    // no guide equivalent and now equals radius-small; `none` exists for
    // flush-joined parts only, never as a visible corner.
    borderRadius: {
        none: '0',
        xs: '4px',     // DS radius-small (tags, list options, nav items)
        sm: '4px',     // = xs (was 8px, off-scale)
        md: '10px',    // DS radius-medium (buttons, inputs)
        lg: '16px',    // DS radius-large
        xl: '20px'     // DS radius-xlarge (standard cards, modals)
    },

    // Rebrand 2026 (#1119): cleverly palettes. Source of truth is the brand repo
    // (D:/Source/cleverly/brand, brand/tokens.json). The ramps below were derived
    // from the 2026-08-28 export; the guide's named colours are mapped onto role
    // tokens in utility.ts (decision log §9).
    // Brand primary = orange.500 #FB6D30 (see semantic.primary).

    // Info Blue scale (DS 'blue' — info states, links in info contexts)
    blue: {
        50: '#F4F9FF',
        100: '#E8F4FF',
        200: '#BDE0FF',
        300: '#7CC0FF',
        400: '#56A6F2',
        500: '#3A8FDF',
        600: '#2B7FD1',
        700: '#2265A8',
        800: '#1D5280',
        900: '#1A4468',
        950: '#102B42'
    },

    // Yellow scale (DS — warnings; statewarning = yellow.700)
    yellow: {
        50: '#FFFBEB',
        100: '#FFF6D1',
        200: '#FFF0B3',
        300: '#FFE27E',
        400: '#FFD24A',
        500: '#F5BB1B',
        600: '#D69E06',
        700: '#B88600',
        800: '#8F6800',
        900: '#6B4E00',
        950: '#3D2C00'
    },

    // Success Green scale (DS — success states; statesuccess = green.500)
    green: {
        50: '#F1FAF4',
        100: '#E8F5EE',
        200: '#C6E7D5',
        300: '#97D2B4',
        400: '#5FB588',
        500: '#2E8B57',
        600: '#287A4C',
        700: '#21613D',
        800: '#1C4E32',
        900: '#17402A',
        950: '#0B2416'
    },

    // Wine scale (DS — serious/error color; stateerror = wine.900 on #FBE6EA).
    // `red` below aliases the same ramp so residual {red.*}/var(--p-red-*) consumers land on-brand.
    wine: {
        50: '#FBF4F6',
        100: '#F5E6EA',
        200: '#E6C9D1',
        300: '#C28E9D',
        400: '#A56C7D',
        500: '#8A5163',
        600: '#6B4050',
        700: '#6B2A3D',
        800: '#5B2233',
        900: '#4F1D2C',
        950: '#33121C'
    },

    // Warm neutral scale (DS 'sand' — light-mode surfaces: 50 = cream page,
    // 200 = warm hairline, 950 = ink). Brand neutrals neutral50…400 alias this.
    sand: {
        0: '#FFFFFF',
        50: '#FAF6F0',
        100: '#F4EFE7',
        200: '#ECE8E2',
        300: '#DAD5CC',
        400: '#B7B1A8',
        500: '#928C83',
        600: '#6E6862',
        700: '#524D48',
        800: '#38342F',
        900: '#23201D',
        950: '#0E0E0E'
    },

    // Cool grey scale (DS — text + form borders; 1:1 with colors_and_type.css --cl-ink-*)
    grey: {
        50: '#F5F5F5',
        100: '#ECECEC',
        200: '#D9D9D9',
        300: '#B3B3B3',
        400: '#949494',
        500: '#767676',
        600: '#4A4A4A',
        700: '#333333',
        800: '#1A1A1A',
        900: '#0E0E0E',
        950: '#000000'
    },

    // Rebrand 2026: 'red' = wine ramp (DS has no red; wine is the error color)
    red: {
        50: '#FBF4F6',
        100: '#F5E6EA',
        200: '#E6C9D1',
        300: '#C28E9D',
        400: '#A56C7D',
        500: '#8A5163',
        600: '#6B4050',
        700: '#6B2A3D',
        800: '#5B2233',
        900: '#4F1D2C',
        950: '#33121C'
    },

    // Rebrand 2026: 'gray' = cool DS grey ramp (text/borders). Warm surfaces
    // moved to 'sand'; brand neutrals use {sand.*} via utility.ts neutral50…400.
    gray: {
        50: '#F5F5F5',
        100: '#ECECEC',
        200: '#D9D9D9',
        300: '#B3B3B3',
        400: '#949494',
        500: '#767676',
        600: '#4A4A4A',
        700: '#333333',
        800: '#1A1A1A',
        900: '#0E0E0E',
        950: '#000000'
    },

    // Slate scale for additional neutrals + bluegray
    slate: {
        50: '#F8FAFC',
        100: '#F1F5F9',
        200: '#E2E8F0',
        300: '#CBD5E1',
        400: '#94A3B8',
        500: '#607D8B',  // exact bluegray from your palette
        600: '#475569',
        700: '#334155',
        800: '#1E293B',
        900: '#0F172A',
        950: '#020617'
    },

    // Brand Orange scale (DS — the new primary; brandmedium = orange.500 #FB6D30)
    orange: {
        50: '#FFF5EF',
        100: '#FFE3D2',
        200: '#FFC8A9',
        300: '#FEA87C',
        400: '#FD8B54',
        500: '#FB6D30',
        600: '#E05820',
        700: '#B94517',
        800: '#923716',
        900: '#762F16',
        950: '#401507'
    },

    // Purple scale + Palette
    purple: {
        50: '#FAF5FF',
        100: '#F3E8FF',
        200: '#E9D5FF',
        300: '#D8B4FE',
        400: '#C084FC',
        500: '#9C27B0',  // exact purple from your palette
        600: '#7C3AED',
        700: '#6D28D9',
        800: '#5B21B6',
        900: '#4C1D95',
        950: '#2E1065'
    },

    // Teal scale + Palette
    teal: {
        50: '#F0FDFA',
        100: '#CCFBF1',
        200: '#99F6E4',
        300: '#5EEAD4',
        400: '#2DD4BF',
        500: '#009688',  // exact teal from your palette
        600: '#0D9488',
        700: '#0F766E',
        800: '#115E59',
        900: '#134E4A',
        950: '#042F2E'
    },

    // Additional palette colors as requested
    cyan: {
        50: '#ECFEFF',
        100: '#CFFAFE',
        200: '#A5F3FC',
        300: '#67E8F9',
        400: '#22D3EE',
        500: '#00BCD4',  // exact color from your palette
        600: '#0891B2',
        700: '#0E7490',
        800: '#155E75',
        900: '#164E63',
        950: '#083344'
    },

    sky: {
        50: '#F0F9FF',
        100: '#E0F2FE',
        200: '#BAE6FD',
        300: '#7DD3FC',
        400: '#38BDF8',
        500: '#0EA5E9',
        600: '#0284C7',
        700: '#0369A1',
        800: '#075985',
        900: '#0C4A6E',
        950: '#082F49'
    },

    pink: {
        50: '#FDF2F8',
        100: '#FCE7F3',
        200: '#FBCFE8',
        300: '#F9A8D4',
        400: '#F472B6',
        500: '#E91E63',  // exact color from your palette
        600: '#DB2777',
        700: '#BE185D',
        800: '#9D174D',
        900: '#831843',
        950: '#500724'
    },

    rose: {
        50: '#FFF1F2',
        100: '#FFE4E6',
        200: '#FECDD3',
        300: '#FDA4AF',
        400: '#FB7185',
        500: '#F43F5E',
        600: '#E11D48',
        700: '#BE123C',
        800: '#9F1239',
        900: '#881337',
        950: '#4C0519'
    },

    fuchsia: {
        50: '#FDF4FF',
        100: '#FAE8FF',
        200: '#F5D0FE',
        300: '#F0ABFC',
        400: '#E879F9',
        500: '#D946EF',
        600: '#C026D3',
        700: '#A21CAF',
        800: '#86198F',
        900: '#701A75',
        950: '#4A044E'
    },

    violet: {
        50: '#F5F3FF',
        100: '#EDE9FE',
        200: '#DDD6FE',
        300: '#C4B5FD',
        400: '#A78BFA',
        500: '#8B5CF6',
        600: '#7C3AED',
        700: '#6D28D9',
        800: '#5B21B6',
        900: '#4C1D95',
        950: '#2E1065'
    },

    indigo: {
        50: '#EEF2FF',
        100: '#E0E7FF',
        200: '#C7D2FE',
        300: '#A5B4FC',
        400: '#818CF8',
        500: '#3F51B5',  // exact color from your palette
        600: '#4F46E5',
        700: '#4338CA',
        800: '#3730A3',
        900: '#312E81',
        950: '#1E1B4B'
    },

    emerald: {
        50: '#ECFDF5',
        100: '#D1FAE5',
        200: '#A7F3D0',
        300: '#6EE7B7',
        400: '#34D399',
        500: '#10B981',
        600: '#059669',
        700: '#047857',
        800: '#065F46',
        900: '#064E3B',
        950: '#022C22'
    },

    lime: {
        50: '#F7FEE7',
        100: '#ECFCCB',
        200: '#D9F99D',
        300: '#BEF264',
        400: '#A3E635',
        500: '#84CC16',
        600: '#65A30D',
        700: '#4D7C0F',
        800: '#3F6212',
        900: '#365314',
        950: '#1A2E05'
    },

    amber: {
        50: '#FFFBEB',
        100: '#FEF3C7',
        200: '#FDE68A',
        300: '#FCD34D',
        400: '#FBBF24',
        500: '#F59E0B',
        600: '#D97706',
        700: '#B45309',
        800: '#92400E',
        900: '#78350F',
        950: '#451A03'
    },

    zinc: {
        50: '#FAFAFA',
        100: '#F4F4F5',
        200: '#E4E4E7',
        300: '#D4D4D8',
        400: '#A1A1AA',
        500: '#71717A',
        600: '#52525B',
        700: '#3F3F46',
        800: '#27272A',
        900: '#18181B',
        950: '#09090B'
    },

    neutral: {
        50: '#FAFAFA',
        100: '#F5F5F5',
        200: '#E5E5E5',
        300: '#D4D4D4',
        400: '#A3A3A3',
        500: '#737373',
        600: '#525252',
        700: '#404040',
        800: '#262626',
        900: '#171717',
        950: '#0A0A0A'
    },

    stone: {
        50: '#FAFAF9',
        100: '#F5F5F4',
        200: '#E7E5E4',
        300: '#D6D3D1',
        400: '#A8A29E',
        500: '#78716C',
        600: '#57534E',
        700: '#44403C',
        800: '#292524',
        900: '#1C1917',
        950: '#0C0A09'
    }
};

export const semantic: AuraBaseTokenSections.Semantic = {
    // ===== CORE SEMANTIC PROPERTIES =====
    transitionDuration: '{extend.transitionDuration.normal}',
    // DS: ONE focus rule for everything focusable — 2px wine outline, 2px offset.
    // (Orange rings reach only 2.9:1, below the 3:1 a focus indicator needs.)
    focusRing: {
        width: '2px',
        style: 'solid',
        color: '{wine.900}',
        offset: '2px',
        shadow: 'none'
    },
    disabledOpacity: '0.45',
    iconSize: '1rem',
    anchorGutter: '0.25rem',

    // ===== PRIMARY COLOR SCALE ===== (rebrand: brand = orange)
    primary: {
        50: '{orange.50}',
        100: '{orange.100}',
        200: '{orange.200}',
        300: '{orange.300}',
        400: '{orange.400}',
        500: '{orange.500}',
        600: '{orange.600}',
        700: '{orange.700}',
        800: '{orange.800}',
        900: '{orange.900}',
        950: '{orange.950}'
    },

    // ===== FORM FIELD CONFIGURATION =====
    formField: {
        paddingX: '{extend.inputpaddingx}',
        paddingY: '{extend.inputpaddingy}',
        sm: {
            fontSize: '0.8125rem',
            paddingX: '12px',
            paddingY: '8px'
        },
        lg: {
            fontSize: '1.0625rem',
            paddingX: '16px',
            paddingY: '13px'
        },
        borderRadius: '{borderRadius.md}',
        focusRing: {
            width: '2px',
            style: 'solid',
            color: '{wine.900}',
            offset: '2px',
            shadow: 'none'
        },
        transitionDuration: '{extend.transitionDuration.normal}'
    },

    // ===== LIST CONFIGURATION =====
    list: {
        padding: '0.5rem',
        gap: '0.125rem',
        header: {
            padding: '0.75rem 1rem'
        },
        option: {
            padding: '0.75rem 1rem',
            borderRadius: '{borderRadius.sm}'
        },
        optionGroup: {
            padding: '0.75rem 1rem',
            fontWeight: '600'
        }
    },

    // ===== CONTENT CONFIGURATION =====
    content: {
        borderRadius: '{borderRadius.lg}'
    },

    // ===== MASK CONFIGURATION =====
    mask: {
        transitionDuration: '{extend.transitionDuration.normal}'
    },

    // ===== NAVIGATION CONFIGURATION =====
    navigation: {
        list: {
            padding: '0.5rem',
            gap: '0.125rem'
        },
        item: {
            padding: '0.75rem 1rem',
            borderRadius: '{borderRadius.sm}',
            gap: '0.5rem'
        },
        submenuLabel: {
            padding: '0.75rem 1rem',
            fontWeight: '300'
        },
        submenuIcon: {
            size: '0.875rem'
        }
    },

    // ===== OVERLAY CONFIGURATION ===== (rebrand: DS shadows/radii)
    overlay: {
        select: {
            borderRadius: '{borderRadius.md}',
            shadow: '{extend.boxshadowlayer1}'
        },
        popover: {
            borderRadius: '{borderRadius.lg}',
            padding: '16px',
            shadow: '{extend.boxshadowlayer1}'
        },
        modal: {
            borderRadius: '{borderRadius.xl}',
            padding: '24px',
            shadow: '0 12px 28px #4f1d2c1a'   // DS shadow-large (max elevation)
        },
        navigation: {
            shadow: '{extend.boxshadowlayer1}'
        }
    },

    // ===== COLOR SCHEMES =====
    // light = Cleverly DS (warm sand surfaces, cool grey text/borders, orange brand).
    // dark = DS-derived wine-dark scheme, DORMANT (darkModeSelector '.app-dark' is
    // never toggled today) — ported from the export for a future dark-mode rollout.
    colorScheme: {
        light: {
            // Surface color scale (warm sand family)
            surface: {
                0: '#FFFFFF',
                50: '{sand.50}',
                100: '{sand.100}',
                200: '{sand.200}',
                300: '{sand.300}',
                400: '{sand.400}',
                500: '{sand.500}',
                600: '{sand.600}',
                700: '{sand.700}',
                800: '{sand.800}',
                900: '{sand.900}',
                950: '{sand.950}'
            },

            // Primary colors
            // Guide (O1): on screen, text on orange is always `wine` (4.75:1);
            // white on orange (2.86:1) is print-only.
            primary: {
                color: '{orange.500}',
                contrastColor: '{wine.900}',
                hoverColor: '{orange.600}',
                activeColor: '{orange.700}'
            },

            // Highlight/selection (O8): orange is a CTA surface only, never a
            // tint. Selection = blue-lightest / blue-lighter with `wine` text.
            highlight: {
                background: '{extend.surfacesoft}',
                focusBackground: '{blue.200}',
                color: '{wine.900}',
                focusColor: '{wine.900}'
            },

            // Mask configuration
            mask: {
                background: '{extend.maskbg}',
                color: '{sand.200}'
            },

            // Form field colors (DS: white bg, cool grey borders, wine focus, wine invalid)
            formField: {
                background: '#FFFFFF',
                disabledBackground: '{grey.100}',
                filledBackground: '{grey.50}',
                filledHoverBackground: '{grey.100}',
                filledFocusBackground: '#FFFFFF',
                borderColor: '{grey.200}',
                hoverBorderColor: '{grey.300}',
                focusBorderColor: '{wine.900}',       // DS: focused fields colour their border wine
                invalidBorderColor: '{wine.900}',
                color: '{text.color}',
                disabledColor: '{grey.300}',
                placeholderColor: '{wine.500}',
                invalidPlaceholderColor: '{wine.700}',
                floatLabelColor: '{wine.500}',
                floatLabelFocusColor: '{wine.900}',
                floatLabelActiveColor: '{wine.600}',
                floatLabelInvalidColor: '{wine.900}',
                iconColor: '{wine.500}',
                shadow: 'none'
            },

            // Text colours (O2): body/UI = `wine-light`, headings = `wine`
            // (extend.textheading), muted = wine.500 (6.1:1 on white). The guide keeps
            // `neutral-darkest` only for the secondary/link button and tags.
            text: {
                color: '{wine.600}',
                hoverColor: '{wine.900}',
                mutedColor: '{wine.500}',
                hoverMutedColor: '{wine.600}'
            },

            // Content colors
            content: {
                background: '#FFFFFF',
                hoverBackground: '{sand.50}',
                borderColor: '{sand.200}',
                color: '{text.color}',
                hoverColor: '{text.hover.color}'
            },

            // Overlay colors
            overlay: {
                select: {
                    background: '#FFFFFF',
                    borderColor: '{sand.200}',
                    color: '{text.color}'
                },
                popover: {
                    background: '#FFFFFF',
                    borderColor: '{sand.200}',
                    color: '{text.color}'
                },
                modal: {
                    background: '#FFFFFF',
                    borderColor: '{sand.200}',
                    color: '{text.color}'
                }
            },

            // List colors
            list: {
                option: {
                    focusBackground: '{sand.100}',
                    selectedBackground: '{extend.surfacesoft}',
                    selectedFocusBackground: '{blue.200}',
                    color: '{text.color}',
                    focusColor: '{text.hover.color}',
                    selectedColor: '{wine.900}',
                    selectedFocusColor: '{wine.900}',
                    icon: {
                        color: '{wine.500}',
                        focusColor: '{wine.900}'
                    }
                },
                optionGroup: {
                    background: 'none',
                    color: '#B54E23'   // DS orange-text (orange.600 fails AA as text)
                }
            },

            // Navigation colors
            navigation: {
                item: {
                    focusBackground: '{sand.100}',
                    activeBackground: '{extend.surfacesoft}',
                    color: '{text.color}',
                    focusColor: '{text.hover.color}',
                    activeColor: '{wine.900}',
                    icon: {
                        color: '{wine.500}',
                        focusColor: '{wine.900}',
                        activeColor: '{wine.900}'
                    }
                },
                submenuLabel: {
                    background: 'none',
                    color: '{text.muted.color}'
                },
                submenuIcon: {
                    color: '{wine.500}',
                    focusColor: '{wine.900}',
                    activeColor: '{wine.900}'
                }
            }
        },

        // DORMANT dark scheme (DS-derived wine-dark; '.app-dark' is never applied)
        dark: {
            surface: {
                0: '#FFFFFF',
                50: '{grey.50}',
                100: '{grey.100}',
                200: '{grey.200}',
                300: '{grey.300}',
                400: '{grey.400}',
                500: '{grey.500}',
                600: '{grey.600}',
                700: '{grey.700}',
                800: '{grey.800}',
                900: '{grey.900}',
                950: '{grey.950}'
            },

            primary: {
                color: '{orange.400}',
                contrastColor: '#0E0E0E',
                hoverColor: '{orange.300}',
                activeColor: '{orange.200}'
            },

            highlight: {
                background: 'rgba(251, 109, 48, 0.16)',
                focusBackground: 'rgba(251, 109, 48, 0.24)',
                color: '{orange.200}',
                focusColor: '{orange.100}'
            },

            mask: {
                background: 'rgba(26, 15, 20, 0.7)',
                color: '{grey.200}'
            },

            formField: {
                background: '{grey.900}',
                disabledBackground: '{grey.800}',
                filledBackground: '{grey.800}',
                filledHoverBackground: '{grey.700}',
                filledFocusBackground: '{grey.900}',
                borderColor: '{grey.700}',
                hoverBorderColor: '{grey.600}',
                focusBorderColor: '{orange.400}',
                invalidBorderColor: '{wine.300}',
                color: '{grey.50}',
                disabledColor: '{grey.500}',
                placeholderColor: '{grey.400}',
                invalidPlaceholderColor: '{wine.300}',
                floatLabelColor: '{grey.400}',
                floatLabelFocusColor: '{orange.400}',
                floatLabelActiveColor: '{grey.300}',
                floatLabelInvalidColor: '{wine.300}',
                iconColor: '{grey.400}',
                shadow: 'none'
            },

            text: {
                color: '{grey.50}',
                hoverColor: '#FFFFFF',
                mutedColor: '{grey.300}',
                hoverMutedColor: '{grey.100}'
            },

            content: {
                background: '{grey.900}',
                hoverBackground: '{grey.800}',
                borderColor: '{grey.700}',
                color: '{text.color}',
                hoverColor: '{text.hover.color}'
            },

            overlay: {
                select: {
                    background: '{grey.900}',
                    borderColor: '{grey.700}',
                    color: '{text.color}'
                },
                popover: {
                    background: '{grey.900}',
                    borderColor: '{grey.700}',
                    color: '{text.color}'
                },
                modal: {
                    background: '{grey.900}',
                    borderColor: '{grey.700}',
                    color: '{text.color}'
                }
            },

            list: {
                option: {
                    focusBackground: '{grey.800}',
                    selectedBackground: 'rgba(251, 109, 48, 0.16)',
                    selectedFocusBackground: 'rgba(251, 109, 48, 0.24)',
                    color: '{text.color}',
                    focusColor: '{text.hover.color}',
                    selectedColor: '{orange.200}',
                    selectedFocusColor: '{orange.100}',
                    icon: {
                        color: '{grey.400}',
                        focusColor: '{grey.300}'
                    }
                },
                optionGroup: {
                    background: 'none',
                    color: '{orange.300}'
                }
            },

            navigation: {
                item: {
                    focusBackground: '{grey.800}',
                    activeBackground: 'rgba(251, 109, 48, 0.16)',
                    color: '{text.color}',
                    focusColor: '{text.hover.color}',
                    activeColor: '{orange.200}',
                    icon: {
                        color: '{grey.400}',
                        focusColor: '{grey.300}',
                        activeColor: '{orange.300}'
                    }
                },
                submenuLabel: {
                    background: 'none',
                    color: '{text.muted.color}'
                },
                submenuIcon: {
                    color: '{grey.400}',
                    focusColor: '{grey.300}',
                    activeColor: '{orange.300}'
                }
            }
        }
    }
};

export default {
    primitive,
    semantic
} satisfies AuraBaseDesignTokens;